import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RegionApiFrom } from '@api-gen/models/region-api-from';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { ForgotPasswordApiRoaming } from '@api-roaming/models/forgot-password-api-roaming';
import { ForgotPasswordEmailApiRoaming } from '@api-roaming/models/forgot-password-email-api-roaming';
import { ListCommunicChannelResponseApiRoaming } from '@api-roaming/models/list-communic-channel-response-api-roaming';
import { ErrorResponse } from '@api/models';
import { RecoverFormField, RecoverFormService } from '@auth/components/recover-form/recover.form.service';
import { SysLimiters } from '@core/limiters/limiters.model';
import { SettingsAsObject } from '@core/models/settings.model';
import { SystemChannelCode } from '@core/models/system-channel-code.model';
import { AppSettingsService } from '@core/services/app-settings.service';
import { TranslateModule } from '@ngx-translate/core';
import { AccountModeType } from '@shared/components/account-mode-switcher/account-mode-switcher.component';
import { CountrySelectorComponent } from '@shared/components/country-selector/country-selector.component';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { IMaskDirective } from 'angular-imask';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';


/**
 * Password recovery form
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-recover-form',
    templateUrl: './recover-form.component.html',
    styleUrls: ['./recover-form.component.scss'],
    standalone: true,
    imports: [
        MatCardModule,
        FormErrorComponent,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        TranslateModule,
        CountrySelectorComponent,
        IMaskDirective,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
    ],
    providers: [RecoverFormService],
})
export class RecoverFormComponent implements OnInit {
    /**
     * Minimum password length
     */
    public readonly passwordMinLength: number = SysLimiters.PasswordMinLength;
    /**
     * List of regions
     */
    @Input() public regions: RegionApiFrom[] = [];
    /**
     * List of country phone codes
     */
    @Input() public countryCodes: CountryCodeApiRoaming[] = [];
    /**
     * List of communication channels
     */
    @Input() public channels: ListCommunicChannelResponseApiRoaming[];

    /**
     * Default number code
     */
    @Input()
    public set defaultCode(code: CountryCodeApiRoaming) {
        if (code) {
            this.codeControl.setValue(code.code);
        }
    }

    @ViewChild('phoneInput') public phoneInput: ElementRef<HTMLInputElement>;
    /**
     * Request process indicator
     */
    @Input() public pending: boolean;
    /**
     * Server Error Object
     */
    @Input() public error: ErrorResponse;

    public fields: {
        [key: string]: string;
    } = {};

    /**
     * Instance settings object from DB
     */
    @Input() public settings: SettingsAsObject;
    /**
     * Form submission
     * @returns form object RecoverData
     */
    @Output() public submitted: EventEmitter<ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming>
        = new EventEmitter<ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming>();

    /**
     * Error generating recaptcha token
     */
    @Output() public errorRecaptcha: EventEmitter<void> = new EventEmitter<void>();

    readonly #recoverFormService: RecoverFormService = inject(RecoverFormService);
    /**
     * Flag hide/show password
     */
    public hidePassword: boolean = true;
    /**
     * PHONE/EMAIL mode flag
     */
    public mode: AccountModeType;

    public readonly systemChannelCode: typeof SystemChannelCode = SystemChannelCode;
    public readonly recoverFormField: typeof RecoverFormField = RecoverFormField;
    public readonly form: FormGroup = this.#recoverFormService.createReactiveForm();

    public get codeControl(): AbstractControl {
        return this.form.get(RecoverFormField.Code);
    }

    public get phoneControl(): AbstractControl {
        return this.form.get(RecoverFormField.Phone);
    }

    public get emailControl(): AbstractControl {
        return this.form.get(RecoverFormField.Email);
    }

    public get passwordControl(): AbstractControl {
        return this.form.get(RecoverFormField.Password);
    }

    public get confirmControl(): AbstractControl {
        return this.form.get(RecoverFormField.ConfirmPassword);
    }

    public get sysCodeControl(): AbstractControl {
        return this.form.get(RecoverFormField.SysCode);
    }

    public ngOnInit(): void {
        this.error = null;
    }

    public onCodeSelect(): void {
        this.phoneInput.nativeElement.focus();
    }

    public onSubmit(): void {
        if (this.appSettingsService.getProp('APP_SETTINGS_RECAPTCHA_KEY')) {
            this.recaptchaV3Service.execute('submitRecoverForm').subscribe(
                (token: string) => {
                    this.generateSendData(token);
                },
                () => {
                    this.errorRecaptcha.emit();
                },
            );
        } else {
            this.generateSendData(null);
        }
    }

    constructor(
        private recaptchaV3Service: ReCaptchaV3Service,
        private appSettingsService: AppSettingsService,
    ) {
    }

    private generateSendData(token: string): void {
        const prefix: string = ('00' + this.codeControl.value).slice(-3);

        this.submitted.emit({

            sysCode: this.sysCodeControl?.value,
            password: this.passwordControl?.value
            ,
            ...(token ? { registrToken: token } : null),
            ...(this.sysCodeControl.value === SystemChannelCode.Sms ? { phoneNumber: prefix + this.phoneControl.value } : null),
            ...(this.sysCodeControl.value === SystemChannelCode.Email ? { email: this.emailControl.value } : null),
        });
    }
}
